import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import './Admin.css';

const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASSWORD || 'ginyboi2024';

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    title: '', category: '', video_url: '', description: '',
    tags: '', featured: false, sort_order: 0,
  });
  const [thumbFile, setThumbFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const fetchProjects = async () => {
    const { data } = await supabase.from('projects').select('*').order('sort_order');
    if (data) setProjects(data);
  };

  useEffect(() => {
    if (authed) fetchProjects();
  }, [authed]);

  const handleLogin = () => {
    if (password === ADMIN_PASS) setAuthed(true);
    else setMessage('Wrong password.');
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setMessage('');
    let thumbnail_url = '';

    if (thumbFile) {
      const ext = thumbFile.name.split('.').pop();
      const filename = `${Date.now()}.${ext}`;
      const { data: upload, error: uploadErr } = await supabase.storage
        .from('thumbnails')
        .upload(filename, thumbFile, { upsert: true });
      if (uploadErr) {
        setMessage('Upload error: ' + uploadErr.message);
        setSaving(false);
        return;
      }
      const { data: publicUrl } = supabase.storage.from('thumbnails').getPublicUrl(filename);
      thumbnail_url = publicUrl.publicUrl;
    }

    const payload = {
      title: form.title,
      category: form.category,
      video_url: form.video_url,
      description: form.description,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      featured: form.featured,
      sort_order: parseInt(form.sort_order) || 0,
      ...(thumbnail_url ? { thumbnail_url } : {}),
    };

    const { error } = await supabase.from('projects').insert([payload]);
    if (error) {
      setMessage('Error: ' + error.message);
    } else {
      setMessage('✓ Project added!');
      setForm({ title: '', category: '', video_url: '', description: '', tags: '', featured: false, sort_order: 0 });
      setThumbFile(null);
      fetchProjects();
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    await supabase.from('projects').delete().eq('id', id);
    fetchProjects();
  };

  if (!authed) {
    return (
      <div className="admin admin--login">
        <h1>Admin Access</h1>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Password"
          onKeyDown={e => e.key === 'Enter' && handleLogin()}
        />
        <button onClick={handleLogin}>Enter</button>
        {message && <p className="admin__msg">{message}</p>}
      </div>
    );
  }

  return (
    <div className="admin">
      <h1 className="admin__title">ADMIN — Add Project</h1>

      <div className="admin__form">
        {[
          ['title', 'Title'],
          ['category', 'Category (e.g. Motion Design)'],
          ['video_url', 'YouTube or Video URL'],
          ['description', 'Description'],
          ['tags', 'Tags (comma separated: Commercials, Short-form, Long-form)'],
          ['sort_order', 'Sort Order (number)'],
        ].map(([name, placeholder]) => (
          <div className="admin__field" key={name}>
            <label>{placeholder}</label>
            <input
              name={name}
              value={form[name]}
              onChange={handleChange}
              placeholder={placeholder}
            />
          </div>
        ))}

        <div className="admin__field">
          <label>
            <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} />
            {' '}Featured on home page
          </label>
        </div>

        <div className="admin__field">
          <label>Thumbnail Image</label>
          <input type="file" accept="image/*" onChange={e => setThumbFile(e.target.files[0])} />
        </div>

        {message && <p className="admin__msg">{message}</p>}

        <button className="admin__submit" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : 'ADD PROJECT'}
        </button>
      </div>

      <h2 className="admin__list-title">Existing Projects</h2>
      <div className="admin__list">
        {projects.map(p => (
          <div key={p.id} className="admin__item">
            <div>
              <strong>{p.title}</strong> — {p.category}
              <span className="admin__item-tags">{(p.tags || []).join(', ')}</span>
            </div>
            <button onClick={() => handleDelete(p.id)} className="admin__delete">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}
